import { createContext, useContext, useState } from "react";
import { getColorBySignerIndex, MarkerColorType } from "@newComponents/common/Viewer/types";
import { DOCUMENT_SIGNER_NO_VERIFY_KEY, ESignSignerSpec } from "@newLibs/ICFAndSign.lib";
import { UseBoolean, useBoolean } from "@newHooks/common/useBoolean";
import { DocumentMarker, useDocumentMarkerContext } from "@newHooks/context/document/documentMarker.context";
import { useDocumentContext } from "../document/document.context";
import {
  DocumentSignerAttr,
  DocumentSignerAttrDateFormat,
  DocumentSignerAttrDateFormatGxpCompatible,
  DocumentSignerAttrTarget,
  DocumentSignerAttrType,
  DocumentSignerReq,
} from "@maven-rest/docs/DocumentSign.dto";
import { uniq } from "lodash";
import { v4 as uuidv4 } from "uuid";

interface ESignSignerContextProps {
  isGxpCompatible: UseBoolean;
  isSignerModified: boolean;
  signers: ESignSigner[];
  setSigners: React.Dispatch<React.SetStateAction<ESignSigner[]>>;
  handlePrepareChangeValue: (
    prev: ESignSigner[],
    uuid: string,
    attr: DocumentSignerAttrType,
    value?: string | number | boolean,
    attrUuid?: string,
    groupUuid?: string
  ) => ESignSigner[];
  handleChangeSignersValue: (
    uuid: string,
    attr: DocumentSignerAttrType,
    value?: string | number | boolean,
    attrUuid?: string,
    groupUuid?: string
  ) => void;
  isRequiredAttrWithGxpCompatible: (attr: DocumentSignerAttrType) => boolean;
  isDynamicSigner: (uuid: string) => boolean;
  isDynamicSignerIncluded: () => boolean;
  handleToggleSignerOpen: (uuid: string) => void;
  handleAddSigner: () => void;
  getSigners: () => DocumentSignerReq[];
  clearSigners: () => void;
}

export type ESignSigner = ESignSignerSpec & {
  isOpenDropdown: boolean;
  signerColor: MarkerColorType;
};

export const defaultUuid = uuidv4();

// NOTE: GXP 호환 모드에서 필수 필드 목록
export const GXP_COMPATIBLE_REQUIRED_ATTR_TYPE_NAMES: DocumentSignerAttrType[] = [
  DocumentSignerAttrType.Signature,
  DocumentSignerAttrType.Name,
  DocumentSignerAttrType.Date,
];

const ESignSignerContext = createContext<ESignSignerContextProps>({
  isGxpCompatible: {
    value: false,
    onTrue: () => {},
    onFalse: () => {},
    onToggle: () => {},
    setValue: () => {},
  },
  isSignerModified: false,
  signers: [],
  setSigners: () => {},
  handlePrepareChangeValue: () => [],
  handleChangeSignersValue: () => {},
  isRequiredAttrWithGxpCompatible: () => false,
  isDynamicSigner: () => false,
  isDynamicSignerIncluded: () => false,
  handleToggleSignerOpen: () => {},
  handleAddSigner: () => {},
  getSigners: () => [],
  clearSigners: () => {},
});

// NOTE: documentContextProvider와 documentMarkerContextProvider 아래에 위치해야 함.
function ESignSignerProvider({ children }: { children: React.ReactNode }) {
  const { setIsShowValueValidity } = useDocumentContext();
  const { markers, setMarkers } = useDocumentMarkerContext();

  const isGxpCompatible = useBoolean(false);
  const isSignerModified = useBoolean(false);
  const [signers, setSigners] = useState<Array<ESignSigner>>(
    (() => {
      if (!markers?.length) return [];

      const findAttr = (signerIndex: number, attr: DocumentSignerAttrType) =>
        markers.find((e) => e.signerIndex === signerIndex && e.attr === attr);

      return uniq(markers.map((e) => e.signerIndex)).map((signerIndex) => ({
        uuid: uuidv4(),
        signerIndex,
        name: findAttr(signerIndex, DocumentSignerAttrType.Name)?.value || "",
        email: findAttr(signerIndex, DocumentSignerAttrType.Email)?.value || "",
        isOpenDropdown: true,
        signerColor: getColorBySignerIndex(signerIndex),
        attrs: markers
          .filter((e) => e.signerIndex === signerIndex)
          .map((attr) => ({
            target: attr.target,
            attr: attr.attr,
            value: attr.value,
            groupUuid: attr.groupUuid,
            attrUuid: attr.attrUuid,
            pageIndex: attr.page,
            coordX: attr.x,
            coordY: attr.y,
          })),
      }));
    })()
  );

  const handlePrepareChangeValue = (
    prev: ESignSigner[],
    uuid: string,
    attr: DocumentSignerAttrType,
    value?: string | number | boolean,
    attrUuid?: string,
    groupUuid?: string
  ) => {
    const signer = prev.find((x) => x.uuid === uuid)!;

    if (attr === DocumentSignerAttrType.Name) signer.name = value === false ? "" : String(value);
    else if (attr === DocumentSignerAttrType.Email) signer.email = value === false ? "" : String(value);
    else if (attr === DocumentSignerAttrType.Company) signer.company = value === false ? "" : String(value);
    else if (attr === DocumentSignerAttrType.Title) signer.title = value === false ? "" : String(value);

    return prev
      .filter((x) => x.uuid !== uuid)
      .concat({
        ...signer,
        attrs: signer.attrs
          .filter((e) => e.attr !== attr || (groupUuid !== undefined && e.attr === attr && e.groupUuid !== groupUuid))
          .concat(
            value === false
              ? []
              : [
                  {
                    target: DocumentSignerAttrTarget.Signer,
                    attr,
                    value: value === true ? "true" : String(value),
                    groupUuid: groupUuid || defaultUuid,
                    attrUuid: attrUuid || defaultUuid,
                    pageIndex: null,
                    coordX: null,
                    coordY: null,
                  },
                ]
          ),
      })
      .sort((a, b) => a.signerIndex - b.signerIndex);
  };

  const handleChangeSignersValue = (
    uuid: string,
    attr: DocumentSignerAttrType,
    value?: string | number | boolean,
    attrUuid?: string,
    groupUuid?: string
  ) => {
    if (!isSignerModified.value) {
      isSignerModified.onTrue();
    }

    setSigners(handlePrepareChangeValue(signers, uuid, attr, value, attrUuid, groupUuid));
  };

  const isRequiredAttrWithGxpCompatible = (attr: DocumentSignerAttrType): boolean => {
    return isGxpCompatible.value && GXP_COMPATIBLE_REQUIRED_ATTR_TYPE_NAMES.includes(attr);
  };

  const isDynamicSigner = (uuid: string): boolean => {
    const signer = signers.find((e) => e.uuid === uuid);
    const dynamicSigner = signer?.attrs.find(
      (x) => x.target === DocumentSignerAttrTarget.Signer && x.attr === DocumentSignerAttrType.DynamicSigner
    );

    if (dynamicSigner?.value) {
      return true;
    }

    return false;
  };

  const isDynamicSignerIncluded = (): boolean => {
    for (const signer of signers) {
      const dynamicSigner = signer.attrs.find(
        (x) => x.target === DocumentSignerAttrTarget.Signer && x.attr === DocumentSignerAttrType.DynamicSigner
      );

      if (dynamicSigner?.value) {
        return true;
      }
    }

    return false;
  };

  const handleToggleSignerOpen = (uuid: string) => {
    setSigners((prev) => prev.map((signer) => (signer.uuid === uuid ? { ...signer, isOpenDropdown: !signer.isOpenDropdown } : signer)));
  };

  const handleAddSigner = () => {
    const uuid = uuidv4();
    const signerIndex = Math.max(...signers.map((e) => e.signerIndex), 0) + 1;

    const signerColor = getColorBySignerIndex(signerIndex);

    const signerIndexMarker: DocumentMarker = {
      signerUuid: uuid,
      target: DocumentSignerAttrTarget.Signer,
      signerIndex,
      signerColor,
      groupUuid: uuidv4(),
      attrUuid: uuidv4(),
      attr: DocumentSignerAttrType.SignerIndex,
      page: null,
      value: undefined,
      x: null,
      y: null,
    };

    // NOTE: Attr 추가시 attr배열에 기존 attr 존재하는 경우는 기존 그대로 사용하고, 없다면 uuidv4() 생성해 uuid유지하기로 논의함 (24.11.29)
    const baseSignerAttrs: DocumentSignerAttr[] = [
      {
        target: DocumentSignerAttrTarget.Signer,
        attr: DocumentSignerAttrType.DateFormat,
        value: !isGxpCompatible.value ? DocumentSignerAttrDateFormat.YYYYMMDD_Hyphen : DocumentSignerAttrDateFormatGxpCompatible[0],
        groupUuid: signerIndexMarker.groupUuid,
        attrUuid: signerIndexMarker.signerUuid,
        pageIndex: null,
        coordX: null,
        coordY: null,
      },
      {
        target: DocumentSignerAttrTarget.Signer,
        attr: DocumentSignerAttrType.SignerIndex,
        value: undefined,
        groupUuid: signerIndexMarker.groupUuid,
        attrUuid: signerIndexMarker.signerUuid,
        pageIndex: null,
        coordX: null,
        coordY: null,
      },
    ];

    if (isGxpCompatible.value) {
      baseSignerAttrs.push({
        target: DocumentSignerAttrTarget.Signer,
        attr: DocumentSignerAttrType.RequireEmailVerify,
        value: "true",
        groupUuid: signerIndexMarker.groupUuid,
        attrUuid: signerIndexMarker.signerUuid,
        pageIndex: null,
        coordX: null,
        coordY: null,
      });
    }

    setSigners(
      signers.concat([
        {
          uuid,
          isOpenDropdown: true,
          signerIndex,
          signerColor,
          name: "",
          email: "",
          attrs: baseSignerAttrs,
        },
      ])
    );

    setMarkers(markers.concat([signerIndexMarker]));
  };

  const getSigners: () => DocumentSignerReq[] = () =>
    signers.map((signer) => ({
      ...signer,
      attrs: signer.attrs
        .filter((e) => !markers.find((m) => m.target === e.target && m.attr === e.attr))
        // NOTE: 인증 불필요 옵션값은(DOCUMENT_SIGNER_NO_VERIFY_KEY) DB enum에 지정되어있지 않으므로, 최종요청 직전에 filter로 제외
        .filter((e) => String(e.attr) !== DOCUMENT_SIGNER_NO_VERIFY_KEY)
        .concat(
          markers
            .filter((x) => x.signerUuid === signer.uuid && ![DocumentSignerAttrType.SignerIndex].includes(x.attr))
            .map((marker) => ({
              target: marker.target,
              attr: marker.attr,
              value: (() => {
                if (marker.target === DocumentSignerAttrTarget.Signer) {
                  if (marker.attr === DocumentSignerAttrType.Name) return signer.name;
                  if (marker.attr === DocumentSignerAttrType.Email) return signer.email;
                  if (marker.attr === DocumentSignerAttrType.Company) return signer.company;
                  if (marker.attr === DocumentSignerAttrType.Title) return signer.title;
                }
                return undefined;
              })(),
              groupUuid: marker.groupUuid,
              attrUuid: marker.attrUuid,
              pageIndex: marker.page,
              coordX: marker.x,
              coordY: marker.y,
            }))
        ),
    }));

  const clearSigners = () => {
    setSigners([]);
    setMarkers([]);
    setIsShowValueValidity(false);
    isSignerModified.onFalse();
  };

  return (
    <ESignSignerContext.Provider
      value={{
        isGxpCompatible,
        isSignerModified: isSignerModified.value,
        signers,
        setSigners,
        handlePrepareChangeValue,
        handleChangeSignersValue,
        isRequiredAttrWithGxpCompatible,
        isDynamicSigner,
        isDynamicSignerIncluded,
        handleToggleSignerOpen,
        handleAddSigner,
        getSigners,
        clearSigners,
      }}
    >
      {children}
    </ESignSignerContext.Provider>
  );
}

function useESignSignerContext() {
  const context = useContext(ESignSignerContext);

  if (!context) {
    throw new Error("Unexpected flow. useESignSignerContext hooks must be used in ESignSignerProvider");
  }

  return context;
}

export { ESignSignerContext, ESignSignerProvider, useESignSignerContext };
